import { z } from 'zod';
import { STICKER_IDS, type StickerId } from './chat';
import { PersonView } from './people';
import { containsBlockedWord } from './profile';

// Gist, as Lagos Life's 📰 Gist (its rules read from the public client code on 26 October 2026), in London
// reality: a social feed. Posts of up to 280 characters with an optional tag (where you are, your home, your
// car, your job) or a sticker; Latest, Trending, Following and Me; likes, comments with replies, re-gists,
// following; report and block; the team takes posts down and writes London Gist news posts. Following as Lagos:
// your contacts are followed for you, anyone can be followed, and unfollowing keeps the contact. Decided with
// Jerry on 26 October 2026. Part 2 (v0.55), in Lagos's newer X-style layout: threads of up to 10 posts, replies,
// share links, search (words, #tags, @names) with the #tags trending in London, Gist's own notifications (likes,
// replies, re-gists, follows, mentions), profiles and bookmarks. Part 3 (v0.56, decided 27 October): voice notes
// as Lagos, on posts and replies, up to 2 minutes, kept 90 days (Messages' voice storage). v0.57 (decided 28
// October): photos from an in-game camera (the game's own view, never the device's camera), one per post, kept
// like billboard pictures and re-encoded on the server; only the poster sees one until a moderator approves it.

export const GIST_POST_MAX = 280;
export const GIST_POST_MIN = 3;
export const GIST_COMMENT_MAX = 200;
export const GIST_PAGE = 30;
// a thread: the first post and up to nine more
export const GIST_THREAD_MAX = 10;
// #tags and @names in what players write
export const GIST_TAG = /#([A-Za-z][A-Za-z0-9_]{1,29})/g;
export const GIST_MENTION = /@([A-Za-z0-9_]{3,20})/g;
export const gistTagsOf = (text: string) =>
  [...new Set([...text.matchAll(GIST_TAG)].map((match) => match[1]!.toLowerCase()))].slice(0, 10);
export const gistMentionsOf = (text: string) =>
  [...new Set([...text.matchAll(GIST_MENTION)].map((match) => match[1]!.toLowerCase()))].slice(0, 10);
export const DEFAULT_TAG_HOURS = 24;
// a voice note on Gist: up to two minutes (as Lagos), kept as long as Messages' (VOICE_KEEP_DAYS)
export const GIST_VOICE_MAX_SECONDS = 120;
// a recording sent but not on a post yet may be used for this long (a photo too)
export const GIST_VOICE_UNUSED_HOURS = 6;
// a photo: as it comes from the game (JPEG, PNG or WebP), and as it is kept (WebP, its longest side at most)
export const GIST_PHOTO = { maxBytes: 3 * 1024 * 1024, maxSide: 1600 } as const;

export const GistContent = z.object({
  visible: z.boolean(),
  // Trending: posts from this many hours, ranked by likes, comments and re-gists, each weighted, halving in weight
  // every `halfLifeHours` so newer posts rise
  trendingHours: z
    .number()
    .int()
    .min(1)
    .max(24 * 14),
  likeWeight: z.number().min(0).max(100),
  commentWeight: z.number().min(0).max(100),
  registWeight: z.number().min(0).max(100),
  halfLifeHours: z
    .number()
    .int()
    .min(1)
    .max(24 * 14),
  // reports from this many players take a post or comment out of sight until the team looks
  reportsToHide: z.number().int().min(1).max(50),
  // posts a player can make in a London day
  postsPerDay: z.number().int().min(1).max(500),
  // Trending in London: the #tags used most in this many hours (missing: 24)
  tagHours: z
    .number()
    .int()
    .min(1)
    .max(24 * 14)
    .optional(),
});
export type GistContent = z.infer<typeof GistContent>;
export const DEFAULT_GIST: GistContent = {
  visible: true,
  trendingHours: 48,
  likeWeight: 1,
  commentWeight: 2,
  registWeight: 3,
  halfLifeHours: 12,
  reportsToHide: 3,
  postsPerDay: 50,
};
export const gistOf = (content: { gist?: GistContent }) => content.gist ?? DEFAULT_GIST;
export function gistProblems(gist: GistContent): string[] {
  const problems: string[] = [];
  if (gist.likeWeight + gist.commentWeight + gist.registWeight <= 0)
    problems.push('Gist: Trending needs at least one weight above nothing.');
  return problems;
}
// a post's Trending score: its weighted activity, halving every `halfLifeHours` of age
export const trendingScore = (
  gist: GistContent,
  post: { likes: number; comments: number; regists: number },
  ageHours: number,
) =>
  (post.likes * gist.likeWeight + post.comments * gist.commentWeight + post.regists * gist.registWeight) *
  0.5 ** (Math.max(0, ageHours) / gist.halfLifeHours);

// ---------- over the wire ----------

// the feeds: For you (latest), Trending, Following, your own posts, your bookmarks, and one player's profile
export const GIST_TABS = ['latest', 'trending', 'following', 'mine', 'bookmarks', 'user'] as const;
export type GistTab = (typeof GIST_TABS)[number];
// what a post can be tagged with: where you are, your home, the car you drive, your job
export const GIST_CHIP_KINDS = ['place', 'home', 'car', 'job'] as const;
export type GistChipKind = (typeof GIST_CHIP_KINDS)[number];
export const GistChip = z.object({ kind: z.enum(GIST_CHIP_KINDS), label: z.string(), emoji: z.string() });
export type GistChip = z.infer<typeof GistChip>;
// a tag you can put on a post now (the server makes the label from your life)
export const GistChipOption = GistChip.extend({ id: z.string() });
export type GistChipOption = z.infer<typeof GistChipOption>;

export const GIST_REPORT_REASONS = {
  harassment: 'Bullying',
  hate: 'Hate',
  sexual: 'Sexual',
  scam: 'Scam',
  selling: 'Selling illegal stuff',
  spam: 'Spam',
  other: 'Other',
} as const;
export type GistReportReason = keyof typeof GIST_REPORT_REASONS;
const reason = z.enum(Object.keys(GIST_REPORT_REASONS) as [GistReportReason, ...GistReportReason[]]);

// a voice note: how long it is, and the address that plays it (null once it has expired)
export const GistVoiceView = z.object({ durationMs: z.number().int(), playPath: z.string().nullable() });
export type GistVoiceView = z.infer<typeof GistVoiceView>;
// a recording just sent, to put on a post or a reply
export const GistVoiceReply = z.object({ voice: GistVoiceView.extend({ id: z.string().uuid() }) });
export type GistVoiceReply = z.infer<typeof GistVoiceReply>;

// a photo on a post: its picture (served by the API) and size; `checking` while a moderator has not approved it
// (only its poster sees it then)
export const GistPhotoView = z.object({
  id: z.string().uuid(),
  src: z.string(),
  width: z.number().int(),
  height: z.number().int(),
  checking: z.boolean(),
});
export type GistPhotoView = z.infer<typeof GistPhotoView>;
export const GistPhotoReply = z.object({ photo: GistPhotoView });
export type GistPhotoReply = z.infer<typeof GistPhotoReply>;

export const GistAuthor = z.object({ lifeId: z.string().uuid(), name: z.string(), look: PersonView.shape.look });
export type GistAuthor = z.infer<typeof GistAuthor>;
export const GistPostView = z.object({
  id: z.string().uuid(),
  // null for a London Gist news post
  author: GistAuthor.nullable(),
  news: z.boolean(),
  body: z.string(),
  sticker: z.enum(STICKER_IDS as [StickerId, ...StickerId[]]).nullable(),
  chip: GistChip.nullable(),
  likes: z.number().int(),
  liked: z.boolean(),
  comments: z.number().int(),
  regists: z.number().int(),
  registed: z.boolean(),
  // in Following and Me, a post someone re-gisted shows who did
  registedBy: z.string().nullable(),
  mine: z.boolean(),
  // you follow its author (so the menu can offer to unfollow)
  following: z.boolean(),
  // held back after reports, waiting for the team (only its author still sees it)
  held: z.boolean(),
  at: z.string(),
  // when it entered this feed (a re-gist's own time), for paging
  feedAt: z.string(),
  // a thread: its first post (null: this is it, or not a thread), this post's place, and how many posts it has
  threadId: z.string().uuid().nullable(),
  part: z.number().int(),
  parts: z.number().int(),
  bookmarked: z.boolean(),
  voice: GistVoiceView.nullable(),
  photo: GistPhotoView.nullable(),
});
export type GistPostView = z.infer<typeof GistPostView>;
// a player on Gist: their profile's top (as Lagos: who, and Follow and Message for someone else)
export const GistProfile = z.object({
  lifeId: z.string().uuid(),
  name: z.string(),
  look: PersonView.shape.look,
  me: z.boolean(),
  following: z.boolean(),
});
export type GistProfile = z.infer<typeof GistProfile>;
export const GistFeedReply = z.object({
  posts: z.array(GistPostView),
  // page on from here (null: the end)
  next: z.string().nullable(),
  // the tags you can put on a post now
  chips: z.array(GistChipOption),
  postsLeftToday: z.number().int(),
  // a profile's top (a player's page, Me and Bookmarks), and Gist notifications not seen yet
  profile: GistProfile.nullable(),
  unread: z.number().int(),
});
export type GistFeedReply = z.infer<typeof GistFeedReply>;
export const GistCommentView = z.object({
  id: z.string().uuid(),
  parentId: z.string().uuid().nullable(),
  author: GistAuthor,
  body: z.string(),
  likes: z.number().int(),
  liked: z.boolean(),
  mine: z.boolean(),
  held: z.boolean(),
  at: z.string(),
  voice: GistVoiceView.nullable(),
});
export type GistCommentView = z.infer<typeof GistCommentView>;
export const GistThreadReply = z.object({
  post: GistPostView,
  // the thread's posts in order (just the post when it is not a thread)
  thread: z.array(GistPostView),
  comments: z.array(GistCommentView),
  message: z.string().optional(),
});
export type GistThreadReply = z.infer<typeof GistThreadReply>;
// after an action: the post and its comments now (no post once it is deleted or reported)
export const GistActionReply = GistThreadReply.extend({ post: GistPostView.nullable() });
export type GistActionReply = z.infer<typeof GistActionReply>;
export const GistFollowReply = z.object({ following: z.boolean(), message: z.string() });
export type GistFollowReply = z.infer<typeof GistFollowReply>;
export const GistPostReply = z.object({ post: GistPostView, message: z.string().optional() });
export type GistPostReply = z.infer<typeof GistPostReply>;

// words a player writes: trimmed, within the limits, through the word filter
const said = (max: number) =>
  z
    .string()
    .trim()
    .min(GIST_POST_MIN, `Say a bit more (${GIST_POST_MIN} characters at least)`)
    .max(max, `Keep it to ${max} characters`)
    .refine((text) => !containsBlockedWord(text), 'Please say it another way');
const maybeSaid = (max: number) => z.union([said(max), z.literal('')]).optional();
const voiceId = z.string().uuid().optional();
// a post after the first in a thread: words, a voice note, or both
const GistPart = z
  .union([
    said(GIST_POST_MAX).transform((text) => ({ text, voice: undefined as string | undefined })),
    z.object({ text: maybeSaid(GIST_POST_MAX), voice: voiceId }).strict(),
  ])
  .refine((part) => Boolean(part.text) || Boolean(part.voice), 'Write something or record a voice note');
export const GistPostRequest = z
  .object({
    text: maybeSaid(GIST_POST_MAX),
    sticker: z.enum(STICKER_IDS as [StickerId, ...StickerId[]]).optional(),
    voice: voiceId,
    // a photo from the in-game camera (the first post only)
    photo: z.string().uuid().optional(),
    chip: z
      .object({ kind: z.enum(GIST_CHIP_KINDS), id: z.string().min(1).max(60) })
      .strict()
      .optional(),
    // more posts after the first, as a thread
    more: z
      .array(GistPart)
      .max(GIST_THREAD_MAX - 1)
      .optional(),
  })
  .strict()
  .refine(
    (body) => Boolean(body.text) || Boolean(body.sticker) || Boolean(body.voice) || Boolean(body.photo),
    'Write something, record a voice note, take a photo or pick a sticker',
  )
  .refine((body) => !(body.sticker && (body.voice || body.photo)), 'A sticker goes on its own');
export type GistPostRequest = z.infer<typeof GistPostRequest>;
export const GistAction = z.discriminatedUnion('action', [
  z.object({ action: z.literal('like'), on: z.boolean() }).strict(),
  z.object({ action: z.literal('regist'), on: z.boolean() }).strict(),
  z
    .object({
      action: z.literal('comment'),
      text: maybeSaid(GIST_COMMENT_MAX),
      parentId: z.string().uuid().optional(),
      voice: voiceId,
    })
    .strict()
    .refine((body) => Boolean(body.text) || Boolean(body.voice), 'Write something or record a voice note'),
  z.object({ action: z.literal('uncomment'), commentId: z.string().uuid() }).strict(),
  z.object({ action: z.literal('comment_like'), commentId: z.string().uuid(), on: z.boolean() }).strict(),
  z.object({ action: z.literal('delete') }).strict(),
  z.object({ action: z.literal('bookmark'), on: z.boolean() }).strict(),
  // another post on the end of your own thread
  z
    .object({ action: z.literal('append'), text: maybeSaid(GIST_POST_MAX), voice: voiceId })
    .strict()
    .refine((body) => Boolean(body.text) || Boolean(body.voice), 'Write something or record a voice note'),
  z.object({ action: z.literal('report'), reason }).strict(),
  z.object({ action: z.literal('report_comment'), commentId: z.string().uuid(), reason }).strict(),
]);
export type GistAction = z.infer<typeof GistAction>;
export const GistFollowRequest = z.object({ on: z.boolean() }).strict();
export type GistFollowRequest = z.infer<typeof GistFollowRequest>;

// ---- search, #tags trending, notifications

export const GIST_SEARCH_KINDS = ['all', 'posts', 'people'] as const;
export type GistSearchKind = (typeof GIST_SEARCH_KINDS)[number];
export const GistTagView = z.object({ tag: z.string(), posts: z.number().int() });
export type GistTagView = z.infer<typeof GistTagView>;
export const GistPersonView = z.object({
  lifeId: z.string().uuid(),
  name: z.string(),
  look: PersonView.shape.look,
  following: z.boolean(),
});
export type GistPersonView = z.infer<typeof GistPersonView>;
export const GistSearchReply = z.object({ posts: z.array(GistPostView), people: z.array(GistPersonView) });
export type GistSearchReply = z.infer<typeof GistSearchReply>;
export const GistTrendsReply = z.object({ tags: z.array(GistTagView) });
export type GistTrendsReply = z.infer<typeof GistTrendsReply>;
export const GIST_NOTICE_KINDS = ['like', 'comment_like', 'regist', 'follow', 'reply', 'mention'] as const;
export type GistNoticeKind = (typeof GIST_NOTICE_KINDS)[number];
// one line in Gist's notifications: who (the latest first; likes and re-gists of one post grouped), on what
export const GistNoticeView = z.object({
  kind: z.enum(GIST_NOTICE_KINDS),
  actors: z.array(GistPersonView.pick({ lifeId: true, name: true, look: true })),
  count: z.number().int(),
  postId: z.string().uuid().nullable(),
  commentId: z.string().uuid().nullable(),
  // a few words of the post or reply it is about
  snippet: z.string(),
  unread: z.boolean(),
  at: z.string(),
});
export type GistNoticeView = z.infer<typeof GistNoticeView>;
export const GistNoticesReply = z.object({ items: z.array(GistNoticeView), unread: z.number().int() });
export type GistNoticesReply = z.infer<typeof GistNoticesReply>;
export const GistUnreadReply = z.object({ unread: z.number().int() });
export type GistUnreadReply = z.infer<typeof GistUnreadReply>;
// "@x and 2 others liked your post"
export function gistNoticeText(notice: Pick<GistNoticeView, 'kind' | 'actors' | 'count'>) {
  const who = `${notice.actors[0]?.name ?? 'Someone'}${
    notice.count > 1 ? ` and ${notice.count - 1} other${notice.count > 2 ? 's' : ''}` : ''
  }`;
  return {
    like: `${who} liked your post`,
    comment_like: `${who} liked your reply`,
    regist: `${who} re-gisted your post`,
    follow: `${who} followed you`,
    reply: `${who} replied`,
    mention: `${who} mentioned you`,
  }[notice.kind];
}

// ---------- the team: reported posts and comments, and London Gist news posts ----------

export const GistReportView = z.object({
  kind: z.enum(['post', 'comment']),
  id: z.string().uuid(),
  postId: z.string().uuid(),
  author: z.object({ lifeId: z.string().uuid(), name: z.string() }),
  body: z.string(),
  sticker: z.string().nullable(),
  // its voice note, kept aside once reported (the team plays it from here), and its photo
  voice: GistVoiceView.nullable(),
  photo: z.object({ src: z.string(), status: z.string() }).nullable(),
  status: z.string(),
  reports: z.array(z.object({ reason: z.string(), by: z.string(), at: z.string() })),
  decided: z.enum(['removed', 'kept']).nullable(),
  at: z.string(),
});
export type GistReportView = z.infer<typeof GistReportView>;
export const GistReportsReply = z.object({ reports: z.array(GistReportView), open: z.number().int() });
export type GistReportsReply = z.infer<typeof GistReportsReply>;
export const GistDecision = z
  .object({
    kind: z.enum(['post', 'comment']),
    outcome: z.enum(['remove', 'keep']),
    note: z.string().trim().max(300).optional(),
  })
  .strict();
export type GistDecision = z.infer<typeof GistDecision>;
export const GistNewsRequest = z.object({ text: z.string().trim().min(GIST_POST_MIN).max(GIST_POST_MAX) }).strict();
export type GistNewsRequest = z.infer<typeof GistNewsRequest>;
export const GistNewsReply = z.object({ news: z.array(GistPostView) });
export type GistNewsReply = z.infer<typeof GistNewsReply>;

// ---- the team: photos waiting for a moderator (and the ones decided lately)

export const GIST_PHOTO_STATUSES = ['checking', 'approved', 'rejected'] as const;
export type GistPhotoStatus = (typeof GIST_PHOTO_STATUSES)[number];
export const GistPhotoQueueItem = z.object({
  id: z.string().uuid(),
  // the picture, from the team's address
  src: z.string(),
  width: z.number().int(),
  height: z.number().int(),
  author: z.object({ lifeId: z.string().uuid(), name: z.string() }),
  // the post it is on, and its words
  postId: z.string().uuid().nullable(),
  postBody: z.string(),
  status: z.enum(GIST_PHOTO_STATUSES),
  note: z.string().nullable(),
  at: z.string(),
  reviewedAt: z.string().nullable(),
});
export type GistPhotoQueueItem = z.infer<typeof GistPhotoQueueItem>;
export const GistPhotosReply = z.object({ photos: z.array(GistPhotoQueueItem), open: z.number().int() });
export type GistPhotosReply = z.infer<typeof GistPhotosReply>;
export const GistPhotoDecision = z
  .object({ outcome: z.enum(['approve', 'reject']), note: z.string().trim().max(300).optional() })
  .strict();
export type GistPhotoDecision = z.infer<typeof GistPhotoDecision>;
